import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CurrencyDollar, ArrowRight, ChartLineUp, Target, Megaphone,
  CursorClick, UsersThree, TrendUp,
} from "@phosphor-icons/react";

// Marketing Intelligence Phase 4 — Paid Advertising.
// Reads /api/marketing/paid. Only surfaces platforms marked as channel_type=paid.

function fmtNum(n) {
  return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function fmtRate(v) {
  return v === null || v === undefined ? "—" : `${v}%`;
}
function fmtRoas(v) {
  return v === null || v === undefined ? "—" : `${v}×`;
}

export default function PaidAdvertising() {
  const { currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/paid", { params: { days: 30 } });
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const t = data?.totals;

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="paid-advertising-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <CurrencyDollar weight="duotone" className="h-3 w-3" /> Marketing · Paid
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Paid advertising</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Aggregated from marketing data on platforms marked as paid channels. Ratios are omitted
            when the denominator is zero — no divide-by-zero noise. ROAS shows only where both ad
            spend and revenue exist for a campaign.
          </p>
          {data?.paid_platforms?.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="paid-platforms-taxonomy">
              <span className="text-[10px] uppercase tracking-widest text-ink-secondary">Paid channels:</span>
              {data.paid_platforms.map((p) => (
                <span key={p} className="text-[10px] uppercase tracking-widest border border-white/10 bg-graphite-950 px-1.5 py-0.5 rounded-sm text-ink-primary/80">
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>
        <Link
          to="/marketing"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-graphite-950 px-4 py-2 text-sm text-ink-primary hover:bg-graphite-900"
          data-testid="paid-advertising-back"
        >
          Marketing overview <ArrowRight weight="bold" className="h-3.5 w-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary" data-testid="paid-advertising-loading">
          Loading paid advertising…
        </div>
      ) : !data ? (
        <div className="mt-10 text-sm text-ink-secondary">Paid advertising unavailable.</div>
      ) : !data.has_data ? (
        <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-12 text-center"
          data-testid="paid-advertising-empty">
          <CurrencyDollar weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/60" />
          <div className="mt-4 font-display text-2xl tracking-tight">No paid advertising data yet</div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            {data.paid_platforms?.length
              ? "Metricool hasn't sent any rows tagged to your paid channels for this window yet."
              : "No channels are currently marked as paid in your workspace's taxonomy."}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-8 text-[10px] uppercase tracking-[0.2em] text-ink-secondary" data-testid="paid-window">
            Last {data.days} days · {data.row_count} row{data.row_count === 1 ? "" : "s"} from paid channels
          </div>

          {/* Totals — 9 metrics laid out on a 3-col grid on md+ */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="paid-totals">
            <StatCard label="Spend" value={fmtMoney(t.spend)} Icon={CurrencyDollar} tone="text-emerald-200" testid="paid-total-spend" mono />
            <StatCard label="Impressions" value={fmtNum(t.impressions)} Icon={ChartLineUp} testid="paid-total-impressions" mono />
            <StatCard label="Clicks" value={fmtNum(t.clicks)} Icon={CursorClick} testid="paid-total-clicks" mono />
            <StatCard label="CTR" value={fmtRate(t.ctr)} sub={t.ctr === null ? "no impressions" : "clicks ÷ impressions"} Icon={Target} tone="text-blue-300" testid="paid-total-ctr" mono />
            <StatCard label="CPC" value={fmtMoney(t.cpc)} sub={t.cpc === null ? "no clicks" : "spend ÷ clicks"} Icon={CurrencyDollar} tone="text-amber-300" testid="paid-total-cpc" mono />
            <StatCard label="Leads" value={fmtNum(t.leads)} Icon={UsersThree} testid="paid-total-leads" mono />
            <StatCard label="CPL" value={fmtMoney(t.cpl)} sub={t.cpl === null ? "no leads" : "spend ÷ leads"} Icon={CurrencyDollar} tone="text-amber-300" testid="paid-total-cpl" mono />
            <StatCard label="Conversions" value={fmtNum(t.conversions)} Icon={TrendUp} tone="text-emerald-300" testid="paid-total-conversions" mono />
            <StatCard label="CPA" value={fmtMoney(t.cpa)} sub={t.cpa === null ? "no conversions" : "spend ÷ conversions"} Icon={CurrencyDollar} tone="text-fuchsia-300" testid="paid-total-cpa" mono />
          </div>

          {/* Per-campaign breakdown */}
          <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6"
            data-testid="paid-by-campaign">
            <div className="flex items-center gap-2">
              <Megaphone weight="duotone" className="h-4 w-4 text-ink-primary/80" />
              <h2 className="font-display text-lg tracking-tight">By campaign</h2>
              <span className="text-[10px] uppercase tracking-widest text-ink-secondary">
                only paid rows tied to a campaign
              </span>
            </div>
            {data.by_campaign.length === 0 ? (
              <div className="mt-4 text-sm text-ink-secondary" data-testid="paid-by-campaign-empty">
                No paid rows are linked to a campaign yet. Rows are linked automatically when the
                Metricool CSV's campaign name matches a campaign in this workspace.
              </div>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                      <th className="px-4 py-2 font-medium">Campaign</th>
                      <th className="px-4 py-2 font-medium text-right">Spend</th>
                      <th className="px-4 py-2 font-medium text-right">Impr.</th>
                      <th className="px-4 py-2 font-medium text-right">Clicks</th>
                      <th className="px-4 py-2 font-medium text-right">CTR</th>
                      <th className="px-4 py-2 font-medium text-right">CPC</th>
                      <th className="px-4 py-2 font-medium text-right">Leads</th>
                      <th className="px-4 py-2 font-medium text-right">CPL</th>
                      <th className="px-4 py-2 font-medium text-right">Conv.</th>
                      <th className="px-4 py-2 font-medium text-right">CPA</th>
                      <th className="px-4 py-2 font-medium text-right">Revenue</th>
                      <th className="px-4 py-2 font-medium text-right">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {data.by_campaign.map((r) => (
                      <tr key={r.campaign_id} data-testid={`paid-campaign-${r.campaign_id}`}
                        className="hover:bg-white/[0.02]">
                        <td className="px-4 py-2 text-ink-primary truncate max-w-[200px]">{r.campaign_name}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-emerald-200">{fmtMoney(r.spend)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-secondary">{fmtNum(r.impressions)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-secondary">{fmtNum(r.clicks)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-blue-300/90">{fmtRate(r.ctr)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-amber-200/90">{fmtMoney(r.cpc)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink-secondary">{fmtNum(r.leads)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-amber-200/90">{fmtMoney(r.cpl)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-emerald-300/90">{fmtNum(r.conversions)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-fuchsia-300/90">{fmtMoney(r.cpa)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-emerald-200">{fmtMoney(r.revenue)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium"
                            data-testid={`paid-campaign-${r.campaign_id}-roas`}
                            data-roas-present={r.roas !== null ? "true" : "false"}>
                          {r.roas === null || r.roas === undefined
                            ? <span className="text-ink-secondary/70">—</span>
                            : <span className={r.roas >= 1 ? "text-emerald-200" : "text-rose-300"}>{fmtRoas(r.roas)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, Icon, tone, testid, mono }) {
  return (
    <div data-testid={testid} className="rounded-md border border-white/10 bg-graphite-900 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-ink-secondary">{label}</div>
        <Icon weight="duotone" className={`h-4 w-4 ${tone || "text-ink-primary/80"}`} />
      </div>
      <div className={`mt-2 font-display text-2xl tracking-tight ${mono ? "tabular-nums" : ""} ${tone || ""}`}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[10px] text-ink-secondary/70">{sub}</div>}
    </div>
  );
}
